import { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import {
  getStoredGuestSession,
  GuestSession,
  GUEST_USAGE_EVENT,
} from "../services/guestSessionService";

export type AuthMode = "loading" | "anon" | "guest" | "user";

export interface AuthState {
  mode: AuthMode;
  guest?: {
    sessionId: string;
    expiresAtMs: number;
    quotaBytes: number;
    /**
     * Client-side view of how much of the quota has been consumed. Seeded from
     * CreateGuestSession (backend storage_used_bytes) and incremented locally as
     * uploads complete. Drives the sidebar quota meter and the shell banner.
     */
    usedBytes: number;
  };
}

const readGuest = (): GuestSession | null => getStoredGuestSession();

/**
 * useAuthMode is the single source of truth for "who is this caller" on the
 * frontend. It collapses Auth0 loading + Auth0 isAuthenticated + the guest
 * session token into a 4-state enum the App shell + sidebar can switch on.
 *
 * usedBytes is seeded from the CreateGuestSession response (0 for a new session,
 * the backend's storage_used_bytes for a resumed one) and kept current
 * client-side: each upload calls incrementGuestUsage(), which persists the new
 * total and dispatches GUEST_USAGE_EVENT so this hook re-reads it.
 */
export const useAuthMode = (): AuthState => {
  const { isLoading, isAuthenticated } = useAuth0();
  const [guest, setGuest] = useState<GuestSession | null>(() => readGuest());

  useEffect(() => {
    // Re-read the guest token whenever Auth0 state flips. This catches the
    // post-login window where the Auth0 callback writes the token and the
    // localStorage flag has not yet been cleared.
    setGuest(readGuest());
  }, [isAuthenticated, isLoading]);

  // Storage events let other tabs that just signed in / out propagate state.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (!e.key || e.key.startsWith("ommuse.guest")) {
        setGuest(readGuest());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // Same-tab usage updates (the native "storage" event only fires in other
  // tabs). incrementGuestUsage dispatches GUEST_USAGE_EVENT after an upload so
  // the meter/banner reflect the new usage without a reload.
  useEffect(() => {
    const handler = () => setGuest(readGuest());
    window.addEventListener(GUEST_USAGE_EVENT, handler);
    return () => window.removeEventListener(GUEST_USAGE_EVENT, handler);
  }, []);

  const state = useMemo<AuthState>(() => {
    if (isLoading) return { mode: "loading" };
    if (isAuthenticated) return { mode: "user" };
    if (guest) {
      return {
        mode: "guest",
        guest: {
          sessionId: guest.sessionId,
          expiresAtMs: guest.expiresAtMs,
          quotaBytes: guest.quotaBytes,
          usedBytes: guest.usedBytes,
        },
      };
    }
    return { mode: "anon" };
  }, [isLoading, isAuthenticated, guest]);

  // Tag every Sentry event with the resolved auth mode so error queries can
  // slice by user / guest / anon. Cheap setTag — only re-runs on transitions.
  useEffect(() => {
    if (state.mode === "loading") return;
    Sentry.setTag("auth_mode", state.mode);
  }, [state.mode]);

  return state;
};
